import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './App.css';

const LEGACY_STORAGE_KEY = 'license-agent-chats-v1';

const apiBase = () => (process.env.REACT_APP_API_URL).replace(/\/$/, '');

async function forgetServerSession(sessionId) {
  if (!sessionId) return;
  try {
    await fetch(`${apiBase()}/api/v1/agent/session/${sessionId}`, { method: 'DELETE' });
  } catch {
    /* best-effort */
  }
}

function parseSseBlocks(buffer) {
  const blocks = buffer.split('\n\n');
  const rest = blocks.pop() || '';
  const events = [];
  for (const block of blocks) {
    const eventMatch = block.match(/^event:\s*(.+)$/m);
    const dataMatch = block.match(/^data:\s*(.+)$/m);
    if (!eventMatch || !dataMatch) continue;
    const name = eventMatch[1].trim();
    try {
      events.push({ name, data: JSON.parse(dataMatch[1]) });
    } catch {
      /* skip malformed */
    }
  }
  return { events, rest };
}

function App() {
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(
    () => () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    },
    []
  );

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const stopRequest = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    setIsLoading(false);
  }, []);

  const handleNewChat = useCallback(() => {
    stopRequest();
    setError('');
    setDraft('');
    setMessages([]);
    setSessionId(null);
    void forgetServerSession(sessionId);
  }, [stopRequest, sessionId]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || isLoading) return;

    setError('');
    setDraft('');
    setIsLoading(true);

    let currentSessionId = sessionId;

    setMessages((prev) => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);

    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      const res = await fetch(`${apiBase()}/api/v1/agent/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: currentSessionId,
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        throw new Error(`Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let sseBuffer = '';

      const appendAssistantDelta = (delta) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next.length - 1;
          if (last >= 0 && next[last].role === 'assistant') {
            next[last] = {
              ...next[last],
              content: (next[last].content || '') + delta,
            };
          }
          return next;
        });
      };

      const setAssistantFull = (full) => {
        setMessages((prev) => {
          const next = [...prev];
          const last = next.length - 1;
          if (last >= 0 && next[last].role === 'assistant') {
            next[last] = { role: 'assistant', content: full };
          }
          return next;
        });
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseSseBlocks(sseBuffer);
        sseBuffer = rest;

        for (const { name, data } of events) {
          if (name === 'session.created' && data.session_id) {
            currentSessionId = data.session_id;
            setSessionId(data.session_id);
          } else if (name === 'message.delta' && data.delta) {
            appendAssistantDelta(data.delta);
          } else if (name === 'message.completed' && typeof data.message === 'string') {
            if (data.session_id) {
              currentSessionId = data.session_id;
              setSessionId(data.session_id);
            }
            setAssistantFull(data.message);
          } else if (name === 'error' && data.message) {
            setError(data.message);
          } else if (name === 'done' && data.session_id) {
            setSessionId(data.session_id);
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Something went wrong');
        setMessages((prev) => {
          const next = [...prev];
          const last = next.length - 1;
          if (last >= 0 && next[last].role === 'assistant' && !next[last].content) {
            next.pop();
          }
          return next;
        });
      }
    } finally {
      controllerRef.current = null;
      setIsLoading(false);
    }
  };

  const onKeyDownComposer = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (draft.trim() && !isLoading) {
        void handleSubmit();
      }
    }
  };

  const canSend = draft.trim().length > 0 && !isLoading;
  const markdownComponents = {
    a: ({ children, ...props }) => (
      <a {...props} target="_blank" rel="noreferrer">
        {children}
      </a>
    ),
  };

  return (
    <div className="app-shell">
      <div className="main">
        <header className="main-header">
          <h1 className="main-title">License verification</h1>
          <button type="button" className="new-chat-btn" onClick={handleNewChat}>
            New chat
          </button>
        </header>

        <div className="messages" role="log" aria-live="polite">
          {!messages.length ? (
            <p className="empty-hint">Start by describing who you need verified, or which state and license.</p>
          ) : (
            messages.map((m, i) => {
              const isLast = i === messages.length - 1;
              const placeholder =
                m.role === 'assistant' && isLoading && isLast && !m.content ? '…' : '';
              return (
                <div key={i} className={`msg msg-${m.role}`}>
                  <div className="msg-label">{m.role === 'user' ? 'You' : 'Assistant'}</div>
                  <div className="msg-body">
                    {m.role === 'assistant' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                      >
                        {m.content || placeholder}
                      </ReactMarkdown>
                    ) : (
                      m.content || placeholder
                    )}
                  </div>
                </div>
              );
            })
          )}
          {isLoading ? (
            <div className="assistant-waiting" aria-live="polite" aria-label="Assistant is searching">
              <span className="assistant-waiting-text">We are searching</span>
              <span className="assistant-waiting-dots" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </div>
          ) : null}
          <div ref={messagesEndRef} />
        </div>

        {error ? <div className="inline-error">{error}</div> : null}

        <footer className="composer-wrap">
          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSend) void handleSubmit(e);
            }}
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDownComposer}
              placeholder="Message… (Enter to send, Shift+Enter for newline)"
              rows={3}
              disabled={isLoading}
              aria-label="Message"
            />
            <div className="composer-actions">
              <button type="submit" disabled={!canSend}>
                Send
              </button>
              <button type="button" className="secondary" onClick={stopRequest} disabled={!isLoading}>
                Stop
              </button>
            </div>
          </form>
        </footer>
      </div>
    </div>
  );
}

export default App;
